// Single source of truth for the team visibility-tier vocabulary in the SPA.
//
// Mirrors the backend `visibility_tier` enum (migration 044 / contract `team-surfaces.ts`):
// work_context < work_plus_skills < full_profile, ordered tier 1 → 3. The backend gates
// what a member's profile exposes by this tier; the SPA only ever DISPLAYS it.
//
// Terum invariant — never hand-type these literals in a component (`=== 'full_profile'`,
// `'tier_2'`, …): import VISIBILITY_TIERS / tierLabel / tierRank from here instead. A
// hand-typed literal that drifts from the DB enum silently hides matching content.

export const VISIBILITY_TIERS = ['work_context', 'work_plus_skills', 'full_profile'] as const;

export type VisibilityTier = (typeof VISIBILITY_TIERS)[number];

export const DEFAULT_VISIBILITY_TIER: VisibilityTier = 'work_context';

const TIER_META: Record<VisibilityTier, { label: string; desc: string }> = {
  work_context: { label: 'Work context only', desc: 'Topics and tools only' },
  work_plus_skills: { label: 'Work + skills', desc: 'Topics, tools, and skill areas' },
  full_profile: { label: 'Full profile', desc: 'All available context' },
};

/** Human label for a tier. Falls back to a de-underscored form for an unknown value. */
export function tierLabel(tier: string): string {
  return TIER_META[tier as VisibilityTier]?.label ?? tier.replace(/_/g, ' ');
}

/** One-line description of what a tier shares. Empty string for an unknown value. */
export function tierDesc(tier: string): string {
  return TIER_META[tier as VisibilityTier]?.desc ?? '';
}

/** 1 = work_context, 2 = work_plus_skills, 3 = full_profile; 0 = unknown. */
export function tierRank(tier: string): number {
  const i = VISIBILITY_TIERS.indexOf(tier as VisibilityTier);
  return i < 0 ? 0 : i + 1;
}
